import { useState, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import Layout from './components/Layout'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Companies from './pages/Companies'
import CompanyDetail from './pages/CompanyDetail'
import Products from './pages/Products'
import ProductDetail from './pages/ProductDetail'
import CardDetail from './pages/CardDetail'
import Orders from './pages/Orders'
import OrderDetail from './pages/OrderDetail'
import OrderClientView from './pages/OrderClientView'
import PreviewLink from './pages/PreviewLink'
import ApproveOrder from './pages/ApproveOrder'
import Settings from './pages/Settings'
import PlaceOrder from './pages/PlaceOrder'
import Help from './pages/Help'
import Catalogue from './pages/Catalogue'
import Activity from './pages/Activity'
import ClientReports from './pages/ClientReports'
import Account from './pages/Account'
import ImportClients from './pages/admin/ImportClients'
import ClientGroups from './pages/admin/ClientGroups'
import ClientGroupDetail from './pages/admin/ClientGroupDetail'
import ProductVisibility from './pages/admin/ProductVisibility'
import Reports from './pages/admin/Reports'
import SetupGuide from './pages/admin/SetupGuide'
import Team from './pages/admin/Team'
import Director from './pages/admin/Director'
import PreviewLinks from './pages/admin/PreviewLinks'
import AccountHealth from './pages/admin/AccountHealth'
import { supabase, isDemoMode } from './lib/supabase'
import { applyBrand, loadBrand, DEFAULT_BRAND } from './lib/brand'
import { loadSessionContext, getActiveCompanyId, setActiveCompanyId } from './lib/session'

export default function App() {
  const [user, setUser] = useState(() => {
    const saved = sessionStorage.getItem('wgop_user')
    return saved ? JSON.parse(saved) : null
  })
  const [brand, setBrand] = useState(DEFAULT_BRAND)
  const [role, setRole] = useState('staff')
  const [companies, setCompanies] = useState([])
  const [activeCompanyId, setActive] = useState(getActiveCompanyId())
  const location = useLocation()

  const isStaff = role === 'staff'
  const isClient = !isStaff
  const isPublicView = location.pathname.startsWith('/v/') || location.pathname.startsWith('/preview/') || location.pathname.startsWith('/p/') || location.pathname.startsWith('/approve/')

  // Staff can preview the client portal via ?preview=<companyId>. The role stays as staff
  // (RLS unchanged) but we render the client layout/routes for the chosen company and pass
  // previewMode=true so submit actions are disabled. The id persists in sessionStorage so
  // in-app navigation (Place Order / My Orders / Help) doesn't drop out of preview.
  const [previewCompanyId, setPreviewCompanyId] = useState(() => sessionStorage.getItem('wgop_preview_company') || null)
  useEffect(() => {
    const fromUrl = new URLSearchParams(location.search).get('preview')
    if (fromUrl) {
      sessionStorage.setItem('wgop_preview_company', fromUrl)
      setPreviewCompanyId(fromUrl)
    }
  }, [location.search])
  const inClientPreview = isStaff && !!previewCompanyId
  const exitPreview = () => {
    sessionStorage.removeItem('wgop_preview_company')
    setPreviewCompanyId(null)
    window.location.assign('/')
  }

  // Auth mode passed down to Login.jsx. Starts from a hint set by the inline
  // script in index.html (which reads invite/recovery tokens from the URL
  // hash before supabase-js consumes them). Can be upgraded to 'invite' by
  // the boot check below if it finds a session for a user who hasn't set a
  // password yet — this is the bulletproof path that doesn't depend on URL
  // fragments surviving email-client redirectors / iOS mail apps / etc.
  const [authMode, setAuthMode] = useState(() => (typeof window !== 'undefined' && window.__authMode) || 'signin')

  // ---- Boot: apply default brand, load global, listen for auth changes
  useEffect(() => {
    applyBrand(DEFAULT_BRAND)
    loadBrand().then(b => { setBrand(b); applyBrand(b) })

    if (isDemoMode || !supabase) return

    // Reconcile React state with the actual supabase session. Three cases:
    //   (1) Session exists AND wgop_user missing — returning user reopened the
    //       tab (sessionStorage is per-tab); hydrate wgop_user from session.
    //   (2) Session exists AND user_metadata.password_set is missing — invite
    //       flow: force them into set-password UI before letting them in.
    //   (3) wgop_user exists BUT no supabase session — stale state (e.g. the
    //       tab kept a demo-mode fake login across the switch to real auth).
    //       Clear the stale hint and bounce them to the login screen so they
    //       sign in cleanly. Without this, updateUser and any authenticated
    //       call silently fails.
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) {
        // No session — but maybe stale wgop_user still hanging around.
        if (sessionStorage.getItem('wgop_user')) {
          sessionStorage.removeItem('wgop_user')
          setUser(null)
        }
        return
      }
      const u = data.session.user
      const passwordSet = !!u.user_metadata?.password_set
      if (!passwordSet) {
        sessionStorage.removeItem('wgop_user')
        setAuthMode('invite')
        return
      }
      if (!sessionStorage.getItem('wgop_user')) {
        const userObj = {
          email: u.email,
          name: u.user_metadata?.name || u.email.split('@')[0],
          id: u.id,
        }
        setUser(userObj)
        sessionStorage.setItem('wgop_user', JSON.stringify(userObj))
      }
    })

    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      if (!session) {
        setUser(null)
        sessionStorage.removeItem('wgop_user')
      }
    })
    return () => sub.subscription?.unsubscribe()
  }, [])

  // ---- After login: load role + companies, then pick brand
  useEffect(() => {
    if (!user) return
    loadSessionContext(user.id).then(ctx => {
      setRole(ctx.role)
      setCompanies(ctx.companies)

      // Choose an active company for client users.
      // Multi-site users (Central Team) default to "All sites" (activeCompanyId
      // = null) so they land on the group rollup. Single-site users always
      // land on their one site. If they had a specific site previously
      // selected (and it's still one they're linked to), honour it.
      let nextActive = activeCompanyId
      if (ctx.role !== 'staff') {
        const hasSelected = nextActive && ctx.companies.find(c => c.id === nextActive)
        if (!hasSelected) {
          nextActive = ctx.companies.length === 1 ? ctx.companies[0].id : null
        }
        setActive(nextActive)
        setActiveCompanyId(nextActive)
        // Only load a per-company brand when we have one; the group brand
        // (loaded at boot) covers "All sites" mode.
        if (nextActive) {
          loadBrand(nextActive).then(b => { setBrand(b); applyBrand(b) })
        }
      }
    })
  }, [user])

  // ---- Re-load brand when client switches site (or staff is previewing a site)
  useEffect(() => {
    const cid = inClientPreview ? previewCompanyId : (isClient ? activeCompanyId : null)
    if (!cid) return
    loadBrand(cid).then(b => { setBrand(b); applyBrand(b) })
  }, [activeCompanyId, isClient, inClientPreview, previewCompanyId])

  // ---- Soft deterrents in client view: no text-select, no image drag, no
  // right-click context menu. CSS handles the first two via body.client-mode;
  // this effect manages the body class and the contextmenu handler.
  useEffect(() => {
    const clientMode = isClient || inClientPreview
    document.body.classList.toggle('client-mode', clientMode)
    if (!clientMode) return
    const blockContextMenu = (e) => e.preventDefault()
    document.addEventListener('contextmenu', blockContextMenu)
    return () => document.removeEventListener('contextmenu', blockContextMenu)
  }, [isClient, inClientPreview])

  const handleLogin = (userData) => {
    setUser(userData)
    sessionStorage.setItem('wgop_user', JSON.stringify(userData))
    // Drop back to signin mode once the user is in, so any subsequent
    // sign-out lands on the normal form instead of the set-password UI.
    setAuthMode('signin')
    if (typeof window !== 'undefined') delete window.__authMode
  }

  const handleLogout = () => {
    setUser(null)
    setRole('staff')
    setCompanies([])
    setActive(null)
    setActiveCompanyId(null)
    sessionStorage.removeItem('wgop_user')
    setAuthMode('signin')
    if (typeof window !== 'undefined') delete window.__authMode
    if (!isDemoMode && supabase) supabase.auth.signOut()
    // Restore default brand on the way back to the login screen.
    loadBrand().then(b => { setBrand(b); applyBrand(b) })
  }

  const switchActiveCompany = (id) => {
    setActive(id)
    setActiveCompanyId(id)
  }

  // ---- Public client view (no auth)
  if (isPublicView) {
    return (
      <Routes>
        <Route path="/v/:token" element={<OrderClientView />} />
        <Route path="/preview/:token" element={<PreviewLink />} />
        {/* /p/ is the short alias; both routes resolve to the same page. */}
        <Route path="/p/:token" element={<PreviewLink />} />
        {/* Head-office approval page — public, single-use token. */}
        <Route path="/approve/:token" element={<ApproveOrder />} />
      </Routes>
    )
  }

  // key forces Login to re-mount when authMode flips (e.g. boot check
  // discovered a session for a user with no password_set), so the form
  // switches from "Sign In" to "Welcome — set a password".
  if (!user) return <Login key={authMode} authMode={authMode} onLogin={handleLogin} brand={brand} />

  // ---- Staff previewing the client portal: render client layout + routes, read-only
  if (inClientPreview) {
    return (
      <Layout
        user={user} onLogout={handleLogout} brand={brand} role="client"
        companies={[]}
        activeCompanyId={previewCompanyId}
        previewMode
        onExitPreview={exitPreview}
      >
        <Routes>
          <Route path="/" element={<Navigate to="/place-order" replace />} />
          <Route path="/place-order" element={<PlaceOrder activeCompanyId={previewCompanyId} previewMode />} />
          <Route path="/catalogue" element={<Catalogue activeCompanyId={previewCompanyId} />} />
          <Route path="/my-orders" element={<Orders activeCompanyId={previewCompanyId} clientMode />} />
          <Route path="/my-orders/:id" element={<OrderDetail clientMode />} />
          <Route path="/activity" element={<Activity activeCompanyId={previewCompanyId} />} />
          <Route path="/reports" element={<ClientReports activeCompanyId={previewCompanyId} />} />
          <Route path="/help" element={<Help />} />
          <Route path="*" element={<Navigate to="/place-order" replace />} />
        </Routes>
      </Layout>
    )
  }

  // ---- Staff routes
  if (isStaff) {
    return (
      <Layout user={user} onLogout={handleLogout} brand={brand} role={role}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/companies" element={<Companies />} />
          <Route path="/companies/:id" element={<CompanyDetail />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/cards/:id" element={<CardDetail />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/orders/:id" element={<OrderDetail />} />
          <Route path="/settings" element={<Settings brand={brand} setBrand={setBrand} />} />
          <Route path="/admin/import-clients" element={<ImportClients />} />
          <Route path="/admin/team" element={<Team />} />
          <Route path="/admin/client-groups" element={<ClientGroups />} />
          <Route path="/admin/client-groups/:id" element={<ClientGroupDetail />} />
          <Route path="/admin/product-visibility" element={<ProductVisibility />} />
          <Route path="/admin/reports" element={<Reports />} />
          <Route path="/admin/setup-guide" element={<SetupGuide />} />
          <Route path="/admin/director" element={<Director />} />
          <Route path="/admin/preview-links" element={<PreviewLinks />} />
          <Route path="/admin/account-health" element={<AccountHealth />} />
          <Route path="/account" element={<Account />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    )
  }

  // ---- Client routes
  return (
    <Layout
      user={user} onLogout={handleLogout} brand={brand} role={role}
      companies={companies}
      activeCompanyId={activeCompanyId}
      onSwitchCompany={switchActiveCompany}
    >
      <Routes>
        <Route path="/" element={<Navigate to="/place-order" replace />} />
        <Route path="/place-order" element={<PlaceOrder activeCompanyId={activeCompanyId} />} />
        <Route path="/catalogue" element={<Catalogue activeCompanyId={activeCompanyId} />} />
        <Route path="/my-orders" element={<Orders activeCompanyId={activeCompanyId} clientMode />} />
        <Route path="/my-orders/:id" element={<OrderDetail clientMode />} />
        <Route path="/activity" element={<Activity activeCompanyId={activeCompanyId} />} />
        <Route path="/reports" element={<ClientReports activeCompanyId={activeCompanyId} />} />
        <Route path="/help" element={<Help />} />
        <Route path="/account" element={<Account />} />
        <Route path="*" element={<Navigate to="/place-order" replace />} />
      </Routes>
    </Layout>
  )
}
